import {useCallback, useEffect, useState} from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Typography,
} from 'antd';
import {EditOutlined} from '@ant-design/icons';
import {useHistory, useParams} from 'react-router-dom';
import {SchoolService} from './SchoolService';
import {Classschool} from '../../graphql/API';
import ProfilePictureCard from '../../components/ProfilePictureCard';
import {UserService} from '../User';
import {StudentsService} from '../Students';
import {UserRole} from '@PikaDoApp/npm-pikado-logic';
import {CurriculumsService} from '../Curriculums/CurriculumsService';
import {LevelsService} from '../Level/LevelsService';
import {GradesService} from '../Grade/GradesService';
import {PackagesService} from '../Package/PackagesService';

const schoolsService = new SchoolService();
const usersService = new UserService();
const studentsService = new StudentsService();
const curriculumsService = new CurriculumsService();
const levelsService = new LevelsService();
const gradesService = new GradesService();
const packagesService = new PackagesService();

interface DataType {
  id: string;
  name: string;
}

const getErrorMessage = (error: any, fallback: string) => {
  const graphQLErrorMessage =
    Array.isArray(error?.errors) && typeof error.errors[0]?.message === 'string'
      ? error.errors[0].message
      : undefined;

  return (
    graphQLErrorMessage ??
    (typeof error?.message === 'string' ? error.message : fallback)
  );
};

const getEntityId = (entity: any) => entity?.id ?? entity?._id;

const getTranslatedName = (value: any) =>
  typeof value === 'string' ? value : (value?.en ?? value?.ar);

const getEntityName = (entity: any, fallback = 'Unnamed') =>
  entity?.name ??
  getTranslatedName(entity?.name_i18n) ??
  entity?.oldId ??
  getEntityId(entity) ??
  fallback;

const getPackageName = (packageItem: any) =>
  getEntityName(
    packageItem,
    packageItem?.packageDuration
      ? `${packageItem.packageDuration} days`
      : 'Unnamed package',
  );

const getPackageProgramId = (packageItem: any) =>
  packageItem?.programId ?? packageItem?.program?.id;

const getSchoolCountry = (schoolData: any) =>
  schoolData?.country ?? schoolData?.user?.country;

const getSchoolTimeZone = (schoolData: any) =>
  schoolData?.timeZone ?? schoolData?.user?.timeZone;

export const SchoolEdit = () => {
  const params: {schoolId: string} = useParams();
  const history = useHistory();
  const {Title, Text} = Typography;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<DataType[]>([]);
  const [school, setSchool] = useState<any>();
  const [isStudentModalOpen, setIsStudentModalOpen] = useState<boolean>(false);
  const [studentForm] = Form.useForm();
  const [classForm] = Form.useForm();
  const [editClassForm] = Form.useForm();
  const [moveStudentForm] = Form.useForm();
  const [selectedClass, setSelectedClass] = useState<DataType | null>(null);
  const [isSavingStudent, setIsSavingStudent] = useState<boolean>(false);
  const [isAddClassModalOpen, setIsAddClassModalOpen] =
    useState<boolean>(false);
  const [isSavingClass, setIsSavingClass] = useState<boolean>(false);
  const [isMoveStudentModalOpen, setIsMoveStudentModalOpen] =
    useState<boolean>(false);
  const [movingSourceClass, setMovingSourceClass] = useState<DataType | null>(
    null,
  );
  const [moveStudentOptions, setMoveStudentOptions] = useState<any[]>([]);
  const [isLoadingMoveStudents, setIsLoadingMoveStudents] =
    useState<boolean>(false);
  const [isMovingStudent, setIsMovingStudent] = useState<boolean>(false);
  const [isDeletingClass, setIsDeletingClass] = useState<boolean>(false);
  const [isEditClassModalOpen, setIsEditClassModalOpen] =
    useState<boolean>(false);
  const [isUpdatingClass, setIsUpdatingClass] = useState<boolean>(false);
  const [editingClass, setEditingClass] = useState<DataType | null>(null);
  const [curriculumOptions, setCurriculumOptions] = useState<any[]>([]);
  const [levelOptions, setLevelOptions] = useState<any[]>([]);
  const [gradeOptions, setGradeOptions] = useState<any[]>([]);
  const [packageOptions, setPackageOptions] = useState<any[]>([]);
  const [isLoadingClassOptions, setIsLoadingClassOptions] =
    useState<boolean>(false);
  const [selectedClassLevelId, setSelectedClassLevelId] = useState<
    string | undefined
  >();
  const [selectedClassGradeId, setSelectedClassGradeId] = useState<
    string | undefined
  >();

  const refreshClasses = useCallback(async () => {
    setIsLoading(true);
    try {
      const classess = await schoolsService.getClassesBySchoolId(
        params.schoolId,
      );
      const newData =
        classess?.items?.map((classS: Classschool) => ({
          name: classS.name,
          id: classS.id,
        })) ?? [];

      setData(newData);
    } catch (error) {
      alert(`Error in fetching classesBySchoolId`);
      console.log('getClassesBySchoolId Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [params.schoolId]);

  const handleStudentModalOk = useCallback(async () => {
    if (!selectedClass) {
      alert('Please select a class before saving.');
      return;
    }

    setIsSavingStudent(true);
    try {
      const values = await studentForm.validateFields();
      const studentData = {
        ...values,
        classSchoolId: selectedClass.id,
        schoolId: params.schoolId,
        birthdate: values.birthdate?.format('YYYY-MM-DD'),
      };
      const schoolCountry = getSchoolCountry(school);
      const schoolTimeZone = getSchoolTimeZone(school);

      if (!schoolCountry || !schoolTimeZone) {
        alert('School country or timezone is missing.');
        return;
      }

      const newUser = await schoolsService.createUser({
        email: studentData.email.trim(),
        password: studentData.password.trim(),
        country: schoolCountry,
        whatsappNumber: studentData.phone.trim(),
        timeZone: schoolTimeZone,
        roles: [UserRole.STUDENT],
      } as any);
      await studentsService.createStudent(
        (newUser as any)._id ?? (newUser as any).id,
        studentData.name,
        studentData.birthdate,
        studentData.age,
        studentData.gender,
        studentData.schoolId,
        true,
        selectedClass.id,
      );

      studentForm.resetFields();
      setSelectedClass(null);
      setIsStudentModalOpen(false);
      alert('Student has been successfully created.');
    } catch (error: any) {
      console.error('Failed to create student:', error);
      if (error?.errorFields) {
        return;
      }

      const errorMessage = getErrorMessage(
        error,
        'An unexpected error occurred while creating the student.',
      );

      alert(`Error creating student: ${errorMessage}`);
    } finally {
      setIsSavingStudent(false);
    }
  }, [studentForm, selectedClass, params.schoolId, school]);

  const handleStudentModalCancel = useCallback(() => {
    setIsStudentModalOpen(false);
    studentForm.resetFields();
    setSelectedClass(null);
  }, [studentForm]);

  const handleAddClassModalOk = useCallback(async () => {
    setIsSavingClass(true);
    try {
      const values = await classForm.validateFields();
      const className = values.name.trim();

      if (!values.levelId && !values.gradeId) {
        alert('Please select either level or grade.');
        return;
      }

      if (values.levelId && values.gradeId) {
        alert('Please select either level or grade, not both.');
        return;
      }

      const selectedPackage = packageOptions.find(
        packageItem => getEntityId(packageItem) === values.packageId,
      );
      const programId = getPackageProgramId(selectedPackage);

      if (!programId) {
        alert('Selected package does not have a program ID.');
        return;
      }

      const schoolCountry = getSchoolCountry(school);
      const schoolTimeZone = getSchoolTimeZone(school);

      if (!schoolCountry || !schoolTimeZone) {
        alert('School country or timezone is missing.');
        return;
      }

      const teacherUser = await schoolsService.adminCreateUser({
        email: values.teacherEmail.trim(),
        password: values.teacherPassword.trim(),
        country: schoolCountry,
        whatsappNumber: values.teacherPhone?.trim(),
        timeZone: schoolTimeZone,
        roles: [UserRole.TEACHER],
      } as any);
      const teacherUserId = getEntityId(teacherUser);

      if (!teacherUserId) {
        alert('Teacher user was created without an ID.');
        return;
      }

      const teacher = await schoolsService.createTeacher({
        userId: teacherUserId,
        name: values.teacherName.trim(),
        schoolId: params.schoolId,
        nationalId: values.teacherNationalId?.trim(),
        gender: values.teacherGender,
      });
      const teacherId = getEntityId(teacher);

      if (!teacherId) {
        alert('Teacher profile was created without an ID.');
        return;
      }

      await schoolsService.createClassSchool({
        schoolId: params.schoolId,
        name: className,
        curriculumId: values.curriculumId,
        packageId: values.packageId,
        levelId: values.levelId,
        gradeId: values.gradeId,
        paymentMethod: 'stripe',
        teacherPrograms: [{teacherId, programId}],
      });
      await refreshClasses();

      classForm.resetFields();
      setSelectedClassLevelId(undefined);
      setSelectedClassGradeId(undefined);
      setIsAddClassModalOpen(false);
      alert('Class has been successfully created.');
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      alert(
        `Error creating class: ${getErrorMessage(
          error,
          'An unexpected error occurred while creating the class.',
        )}`,
      );
    } finally {
      setIsSavingClass(false);
    }
  }, [classForm, packageOptions, params.schoolId, refreshClasses, school]);

  const handleAddClassModalCancel = useCallback(() => {
    setIsAddClassModalOpen(false);
    classForm.resetFields();
    setSelectedClassLevelId(undefined);
    setSelectedClassGradeId(undefined);
  }, [classForm]);

  const openEditClassModal = useCallback(
    (classItem: DataType) => {
      setEditingClass(classItem);
      editClassForm.setFieldsValue({name: classItem.name});
      setIsEditClassModalOpen(true);
    },
    [editClassForm],
  );

  const handleEditClassModalOk = useCallback(async () => {
    if (!editingClass) {
      alert('Please select a class before updating.');
      return;
    }

    setIsUpdatingClass(true);
    try {
      const values = await editClassForm.validateFields();
      const className = values.name.trim();

      await schoolsService.updateClassSchool({
        id: editingClass.id,
        name: className,
      });
      await refreshClasses();

      editClassForm.resetFields();
      setEditingClass(null);
      setIsEditClassModalOpen(false);
      alert('Class name has been updated.');
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      alert(
        `Error updating class: ${getErrorMessage(
          error,
          'An unexpected error occurred while updating the class.',
        )}`,
      );
    } finally {
      setIsUpdatingClass(false);
    }
  }, [editClassForm, editingClass, refreshClasses]);

  const handleEditClassModalCancel = useCallback(() => {
    setIsEditClassModalOpen(false);
    setEditingClass(null);
    editClassForm.resetFields();
  }, [editClassForm]);

  const openMoveStudentModal = useCallback(
    async (sourceClass: DataType) => {
      setMovingSourceClass(sourceClass);
      setMoveStudentOptions([]);
      moveStudentForm.resetFields();
      setIsMoveStudentModalOpen(true);
      setIsLoadingMoveStudents(true);

      try {
        const students = await studentsService.getStudentsByClassId(
          sourceClass.id,
        );
        setMoveStudentOptions(students ?? []);
      } catch (error: any) {
        alert(
          `Error loading students: ${getErrorMessage(
            error,
            'Failed to load students for this class.',
          )}`,
        );
      } finally {
        setIsLoadingMoveStudents(false);
      }
    },
    [moveStudentForm],
  );

  const handleMoveStudentModalOk = useCallback(async () => {
    if (!movingSourceClass) {
      alert('Please select a source class first.');
      return;
    }

    setIsMovingStudent(true);
    try {
      const values = await moveStudentForm.validateFields();
      const selectedStudent = moveStudentOptions.find(
        student => (student.id ?? student._id) === values.studentId,
      );

      await studentsService.updateStudent({
        ...(selectedStudent ?? {}),
        id: values.studentId,
        schoolId: params.schoolId,
        classSchoolId: values.classSchoolId,
        classschoolId: values.classSchoolId,
        classschool: {id: values.classSchoolId},
      });

      moveStudentForm.resetFields();
      setMovingSourceClass(null);
      setMoveStudentOptions([]);
      setIsMoveStudentModalOpen(false);
      alert('Student has been successfully moved.');
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      alert(
        `Error moving student: ${getErrorMessage(
          error,
          'An unexpected error occurred while moving the student.',
        )}`,
      );
    } finally {
      setIsMovingStudent(false);
    }
  }, [moveStudentForm, moveStudentOptions, movingSourceClass, params.schoolId]);

  const handleMoveStudentModalCancel = useCallback(() => {
    setIsMoveStudentModalOpen(false);
    setMovingSourceClass(null);
    setMoveStudentOptions([]);
    moveStudentForm.resetFields();
  }, [moveStudentForm]);

  const handleDeleteClassSchool = useCallback(
    (classItem: DataType) => {
      Modal.confirm({
        title: 'Delete class?',
        content:
          'This will remove all students from the class, soft delete class enrollments, create free enrollments for removed students, and soft delete the class.',
        okText: 'Delete',
        okType: 'danger',
        onOk: async () => {
          setIsDeletingClass(true);
          try {
            await schoolsService.deleteClassSchool(classItem.id);
            await refreshClasses();
            alert('Class has been deleted.');
          } catch (error: any) {
            alert(
              `Error deleting class: ${getErrorMessage(
                error,
                'Failed to delete class.',
              )}`,
            );
          } finally {
            setIsDeletingClass(false);
          }
        },
      });
    },
    [refreshClasses],
  );

  useEffect(() => {
    schoolsService
      .getSchoolById(params.schoolId)
      .then(async schoolProfile => {
        const schoolUserId =
          getEntityId((schoolProfile as any)?.user) ?? schoolProfile?.userId;
        const schoolUser = schoolUserId
          ? await schoolsService.getUserById(schoolUserId)
          : undefined;

        setSchool({
          ...schoolProfile,
          user: schoolUser,
          country: schoolUser?.country,
          timeZone: schoolUser?.timeZone,
        });
      })
      .catch(e => {
        alert(`Error in getSchoolById: ${e}`);
      });
    refreshClasses();
  }, [params.schoolId, refreshClasses]);

  useEffect(() => {
    let isMounted = true;

    const loadClassOptions = async () => {
      setIsLoadingClassOptions(true);
      try {
        const [curriculums, levels, grades, packagesResponse] =
          await Promise.all([
            curriculumsService.getCurriculums(),
            levelsService.getLevels(),
            gradesService.getGrades(),
            packagesService.getPackages(1, 100),
          ]);

        if (!isMounted) {
          return;
        }

        setCurriculumOptions(curriculums ?? []);
        setLevelOptions(levels ?? []);
        setGradeOptions(grades ?? []);
        setPackageOptions(
          (packagesResponse?.items ?? []).filter(
            packageItem => !packageItem.isDeleted && packageItem.isActive,
          ),
        );
      } catch (error: any) {
        console.error('Failed to load class enrollment options:', error);
        if (isMounted) {
          alert(
            `Error loading class enrollment options: ${getErrorMessage(
              error,
              'Failed to load curriculums, levels, grades, or packages.',
            )}`,
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingClassOptions(false);
        }
      }
    };

    loadClassOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div style={{padding: 10}}>
      {school?.logoUrl ? (
        <div>
          <p>School logo:</p>
          <ProfilePictureCard thumbnailUri={school.logoUrl} />
        </div>
      ) : (
        <p>No school logo provided</p>
      )}
      <Title level={2} style={{textAlign: 'center', marginBottom: 4}}>
        {school?.name ?? 'School'}{' '}
        <Text type="secondary" style={{fontSize: 16}}>
          ({params.schoolId})
        </Text>
      </Title>
      <Title level={4} style={{textAlign: 'center', marginTop: 0}}>
        Classes
      </Title>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 12,
        }}>
        <Button
          type="primary"
          loading={isLoading}
          onClick={() => setIsAddClassModalOpen(true)}>
          Add Class
        </Button>
      </div>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}>
        <thead>
          <tr>
            <th
              style={{
                border: '1px solid #ddd',
                padding: '12px',
                textAlign: 'left',
                width: '28%',
              }}>
              Class Name
            </th>
            <th
              style={{
                border: '1px solid #ddd',
                padding: '12px',
                textAlign: 'left',
                width: '42%',
              }}>
              Class ID
            </th>
            <th
              style={{
                border: '1px solid #ddd',
                padding: '12px',
                textAlign: 'left',
                width: '30%',
              }}>
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map(item => (
            <tr key={item.id}>
              <td
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  fontWeight: 'bold',
                  fontSize: 16,
                  verticalAlign: 'middle',
                }}>
                <Button
                  type="link"
                  style={{padding: 0, height: 'auto', fontWeight: 'bold'}}
                  onClick={() => history.push(`/class-schools/${item.id}`)}>
                  {item.name}
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  aria-label="Update class name"
                  title="Update class name"
                  onClick={() => openEditClassModal(item)}
                />
              </td>
              <td
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  verticalAlign: 'middle',
                  wordBreak: 'break-all',
                }}>
                {item.id}
              </td>
              <td
                style={{
                  border: '1px solid #ddd',
                  padding: '12px',
                  verticalAlign: 'middle',
                }}>
                <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => {
                      setIsStudentModalOpen(true);
                      setSelectedClass(item);
                    }}>
                    Add Student
                  </Button>
                  <Button
                    type="default"
                    size="small"
                    disabled={data.length < 2}
                    onClick={() => openMoveStudentModal(item)}>
                    Move Student
                  </Button>
                  <Button
                    danger
                    type="default"
                    size="small"
                    loading={isDeletingClass}
                    onClick={() => handleDeleteClassSchool(item)}>
                    Delete Class
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        title="Add Class"
        open={isAddClassModalOpen}
        onOk={handleAddClassModalOk}
        onCancel={handleAddClassModalCancel}
        width={640}
        okText="Create"
        okButtonProps={{
          loading: isSavingClass,
          disabled: isLoadingClassOptions,
        }}>
        <Form form={classForm} layout="vertical">
          <Form.Item
            label="Class Name"
            name="name"
            normalize={value => (typeof value === 'string' ? value : '')}
            rules={[
              {required: true, message: 'Please enter class name'},
              {
                validator: (_, value) =>
                  value?.trim()
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter class name')),
              },
            ]}>
            <Input placeholder="Enter class name" />
          </Form.Item>
          <Form.Item
            label="Curriculum"
            name="curriculumId"
            rules={[{required: true, message: 'Please select curriculum'}]}>
            <Select
              loading={isLoadingClassOptions}
              showSearch
              placeholder="Select curriculum"
              optionFilterProp="children">
              {curriculumOptions.map(curriculum => {
                const curriculumId = getEntityId(curriculum);
                return (
                  <Select.Option key={curriculumId} value={curriculumId}>
                    {getEntityName(curriculum)}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            label="Package"
            name="packageId"
            rules={[{required: true, message: 'Please select package'}]}>
            <Select
              loading={isLoadingClassOptions}
              showSearch
              placeholder="Select package"
              optionFilterProp="children">
              {packageOptions.map(packageItem => {
                const packageId = getEntityId(packageItem);
                return (
                  <Select.Option key={packageId} value={packageId}>
                    {getPackageName(packageItem)}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item label="Level" name="levelId">
            <Select
              allowClear
              loading={isLoadingClassOptions}
              onChange={value => {
                setSelectedClassLevelId(value);
                if (value) {
                  classForm.setFieldsValue({gradeId: undefined});
                  setSelectedClassGradeId(undefined);
                }
              }}
              showSearch
              placeholder="Select level"
              optionFilterProp="children">
              {levelOptions.map(level => {
                const levelId = getEntityId(level);
                return (
                  <Select.Option key={levelId} value={levelId}>
                    {getEntityName(level)}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item label="Grade" name="gradeId">
            <Select
              allowClear
              loading={isLoadingClassOptions}
              onChange={value => {
                setSelectedClassGradeId(value);
                if (value) {
                  classForm.setFieldsValue({levelId: undefined});
                  setSelectedClassLevelId(undefined);
                }
              }}
              showSearch
              placeholder="Select grade"
              optionFilterProp="children">
              {gradeOptions.map(grade => {
                const gradeId = getEntityId(grade);
                return (
                  <Select.Option key={gradeId} value={gradeId}>
                    {getEntityName(grade)}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Title level={5} style={{marginTop: 8}}>
            Teacher
          </Title>
          <Form.Item
            label="Teacher Name"
            name="teacherName"
            normalize={value => (typeof value === 'string' ? value : '')}
            rules={[
              {required: true, message: 'Please enter teacher name'},
              {
                validator: (_, value) =>
                  value?.trim()
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter teacher name')),
              },
            ]}>
            <Input placeholder="Enter teacher name" />
          </Form.Item>
          <Form.Item
            label="Teacher Email"
            name="teacherEmail"
            normalize={value => (typeof value === 'string' ? value : '')}
            rules={[
              {required: true, message: 'Please enter teacher email'},
              {type: 'email', message: 'Please enter a valid email'},
              {
                validator: (_, value) =>
                  value?.trim()
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter teacher email')),
              },
            ]}>
            <Input type="email" placeholder="Enter teacher email" />
          </Form.Item>
          <Form.Item
            label="Teacher Password"
            name="teacherPassword"
            normalize={value => (typeof value === 'string' ? value : '')}
            rules={[
              {required: true, message: 'Please enter teacher password'},
              {
                validator: (_, value) =>
                  value?.trim()
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Please enter teacher password'),
                      ),
              },
            ]}>
            <Input.Password placeholder="Enter teacher password" />
          </Form.Item>
          <Form.Item
            label="Teacher Phone Number"
            name="teacherPhone"
            normalize={value => (typeof value === 'string' ? value : '')}>
            <Input placeholder="Enter teacher phone number" />
          </Form.Item>
          <Form.Item
            label="Teacher National ID"
            name="teacherNationalId"
            normalize={value => (typeof value === 'string' ? value : '')}>
            <Input placeholder="Enter teacher national ID" />
          </Form.Item>
          <Form.Item label="Teacher Gender" name="teacherGender">
            <Select allowClear placeholder="Select teacher gender">
              <Select.Option value="FEMALE">Female</Select.Option>
              <Select.Option value="MALE">Male</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`Update Class Name${editingClass ? ` - ${editingClass.name}` : ''}`}
        open={isEditClassModalOpen}
        onOk={handleEditClassModalOk}
        onCancel={handleEditClassModalCancel}
        okText="Update"
        okButtonProps={{loading: isUpdatingClass}}>
        <Form form={editClassForm} layout="vertical">
          <Form.Item
            label="Class Name"
            name="name"
            normalize={value => (typeof value === 'string' ? value : '')}
            rules={[
              {required: true, message: 'Please enter class name'},
              {
                validator: (_, value) =>
                  value?.trim()
                    ? Promise.resolve()
                    : Promise.reject(new Error('Please enter class name')),
              },
            ]}>
            <Input placeholder="Enter class name" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`Move Student${movingSourceClass ? ` from ${movingSourceClass.name}` : ''}`}
        open={isMoveStudentModalOpen}
        onOk={handleMoveStudentModalOk}
        onCancel={handleMoveStudentModalCancel}
        okText="Move"
        okButtonProps={{
          loading: isMovingStudent,
          disabled:
            isLoadingMoveStudents ||
            data.filter(classS => classS.id !== movingSourceClass?.id)
              .length === 0 ||
            moveStudentOptions.length === 0,
        }}>
        <Form form={moveStudentForm} layout="vertical">
          <Form.Item
            label="Student"
            name="studentId"
            rules={[{required: true, message: 'Please select a student'}]}>
            <Select
              loading={isLoadingMoveStudents}
              disabled={
                isLoadingMoveStudents || moveStudentOptions.length === 0
              }
              showSearch
              placeholder={
                isLoadingMoveStudents
                  ? 'Loading students...'
                  : 'Select a student'
              }
              optionFilterProp="children">
              {moveStudentOptions.map(student => {
                const studentId = student.id ?? student._id;
                return (
                  <Select.Option key={studentId} value={studentId}>
                    {student.name ?? 'Unnamed student'} ({studentId})
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            label="Destination Class"
            name="classSchoolId"
            rules={[
              {required: true, message: 'Please select destination class'},
            ]}>
            <Select
              disabled={data.length < 2}
              showSearch
              placeholder="Select destination class"
              optionFilterProp="children">
              {data
                .filter(classS => classS.id !== movingSourceClass?.id)
                .map(classS => (
                  <Select.Option key={classS.id} value={classS.id}>
                    {classS.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`Add Student ${selectedClass?.name ?? ''}`}
        open={isStudentModalOpen}
        onOk={handleStudentModalOk}
        onCancel={handleStudentModalCancel}
        okText="Save"
        okButtonProps={{loading: isSavingStudent}}>
        <Form
          form={studentForm}
          layout="vertical"
          initialValues={{gender: 'FEMALE'}}>
          <Form.Item
            label="Student Name"
            name="name"
            rules={[{required: true, message: 'Please enter student name'}]}>
            <Input placeholder="Enter student name" />
          </Form.Item>
          <Form.Item
            label="Age"
            name="age"
            rules={[{required: true, message: 'Please enter age'}]}>
            <InputNumber
              style={{width: '100%'}}
              min={1}
              placeholder="Enter age"
            />
          </Form.Item>
          <Form.Item
            label="Birthdate"
            name="birthdate"
            rules={[{required: true, message: 'Please select birthdate'}]}>
            <DatePicker style={{width: '100%'}} />
          </Form.Item>
          <Form.Item
            label="Gender"
            name="gender"
            rules={[{required: true, message: 'Please select gender'}]}>
            <Select>
              <Select.Option value="FEMALE">Female</Select.Option>
              <Select.Option value="MALE">Male</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="Phone Number"
            name="phone"
            rules={[{required: true, message: 'Please enter phone number'}]}>
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[{required: true, message: 'Please enter email'}]}>
            <Input type="email" placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{required: true, message: 'Please enter password'}]}>
            <Input.Password placeholder="Enter password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
